import React from 'react';
import { ThreadListProps } from '@dreamai/types';
import { formatDistanceToNow } from 'date-fns';

import './thread-list.scss';

interface ThreadListComponentProps extends ThreadListProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

const ThreadList: React.FC<ThreadListComponentProps> = ({
  threads,
  title,
  timePeriod,
}) => {
  const handleThreadClick = (threadId: string, agentId: string) => {
    if (agentId && threadId) {
      window.open(
        `/conversations?agent_id=${agentId}&thread_id=${threadId}`,
        '_blank',
      );
    }
  };

  const getAgentTypeColor = (agentType: string): string => {
    const colors: Record<string, string> = {
      'Business Manager': '#4CAF50',
      'Sales Manager': '#2196F3',
      'Finance Manager': '#9C27B0',
      'Marketing Manager': '#FF9800',
      'Admin Manager': '#607D8B',
      'System Manager': '#F44336',
    };
    return colors[agentType] || '#757575';
  };

  if (threads.length === 0) {
    return (
      <div className='thread-list empty'>
        <h3>{title}</h3>
        {timePeriod && <p className='time-period'>{timePeriod}</p>}
        <p className='no-threads'>No threads found</p>
      </div>
    );
  }

  return (
    <div className='thread-list'>
      <div className='thread-list-header'>
        <h3>{title}</h3>
        {timePeriod && <span className='time-period'>{timePeriod}</span>}
        <span className='thread-count'>{threads.length} threads</span>
      </div>

      <div className='thread-items'>
        {threads.map((thread) => (
          <div
            key={thread.threadId}
            className={`thread-item ${thread.isMainThread ? 'main-thread' : ''}`}
            onClick={() => handleThreadClick(thread.threadId, thread.agentId)}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleThreadClick(thread.threadId, thread.agentId);
              }
            }}
          >
            <div className='thread-item-header'>
              <span
                className='agent-type-badge'
                style={{ backgroundColor: getAgentTypeColor(thread.agentType) }}
              >
                {thread.agentType}
              </span>
              {thread.isMainThread && (
                <span className='main-thread-badge'>Main Thread</span>
              )}
            </div>

            <div className='thread-item-content'>
              <p className='thread-description'>{thread.description}</p>

              <div className='thread-meta'>
                {thread.userName && (
                  <span className='user-name'>{thread.userName}</span>
                )}
                {thread.companyName && (
                  <>
                    <span className='separator'>•</span>
                    <span className='company-name'>{thread.companyName}</span>
                  </>
                )}
                <span className='separator'>•</span>
                <span className='last-updated'>
                  {formatDistanceToNow(new Date(thread.lastUpdatedAt), {
                    addSuffix: true,
                  })}
                </span>
              </div>
            </div>

            <div className='thread-item-arrow'>→</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThreadList;
